import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { 
  reportsService, 
  BirdCertificate,
  BirdListing,
  BreedingList,
  BalanceSheet,
  IncomeStatement,
  InventoryReport,
  ReportFilters 
} from '../services/reports';

// Hook for Bird Certificate
export function useBirdCertificate(birdId: string | null) {
  const [data, setData] = useState<BirdCertificate | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!birdId) return;
    
    try {
      setLoading(true);
      setError(null);
      const result = await reportsService.getBirdCertificate(birdId);
      setData(result);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load bird certificate';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [birdId]);

  useEffect(() => {
    if (birdId) {
      load();
    }
  }, [load, birdId]);

  return { data, loading, error, reload: load };
}

// Hook for Birds Listing
export function useBirdsListing(filters?: ReportFilters) {
  const [data, setData] = useState<BirdListing | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await reportsService.getBirdsListing(filters);
      setData(result);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load birds listing';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, error, reload: load };
}

// Hook for Breeding List
export function useBreedingList(filters?: ReportFilters) {
  const [data, setData] = useState<BreedingList | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await reportsService.getBreedingList(filters);
      setData(result);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load breeding list';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, error, reload: load };
}

// Hook for Balance Sheet
export function useBalanceSheet(asOfDate?: string) {
  const [data, setData] = useState<BalanceSheet | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await reportsService.getBalanceSheet(asOfDate);
      setData(result);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load balance sheet';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [asOfDate]);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, error, reload: load };
}

// Hook for Income Statement
export function useIncomeStatement(filters?: ReportFilters) {
  const [data, setData] = useState<IncomeStatement | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await reportsService.getIncomeStatement(filters);
      setData(result);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load income statement';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, error, reload: load };
}

// Hook for Inventory Report
export function useInventoryReport(asOfDate?: string) {
  const [data, setData] = useState<InventoryReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await reportsService.getInventoryReport(asOfDate);
      setData(result);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load inventory report';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [asOfDate]);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, error, reload: load };
} 